/**
 * SeatTemplateTools.jsx - Template-Based Seat Placement System
 * Elvis Aloha Collection - Map Editor Studio
 * 
 * Provides:
 * - Template selection
 * - Click-to-place seats using template
 * - Rotation controls
 * - Clone/duplicate functionality
 * - Row generation
 * - Consistent seat sizes
 * 
 * ARCHITECTURE:
 * - Templates define consistent width/height
 * - Placed seats reference templates by ID + revision
 * - Rotation is editable, size is locked by default
 * - Coordinates are native chart-image pixels
 */

import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook for managing seat templates
 */
export const useSeatTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    if (loading) return; // Prevent duplicate fetches
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/seat-templates`);
      if (res.ok) {
        const data = await res.json();
        const fetchedTemplates = data.templates || [];
        setTemplates(fetchedTemplates);
        
        // Set default template as active only on first load
        if (!initialized && fetchedTemplates.length > 0) {
          const defaultTpl = fetchedTemplates.find(t => t.is_default) || fetchedTemplates[0];
          setActiveTemplate(defaultTpl);
          setInitialized(true);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loading, initialized]);

  useEffect(() => {
    fetchTemplates();
  }, []); // Only run once on mount

  return {
    templates,
    activeTemplate,
    setActiveTemplate,
    loading,
    error,
    refreshTemplates: fetchTemplates
  };
};

/**
 * Hook for managing placed seats
 * Uses credentials: 'include' to automatically send HTTP-only auth cookies
 */
export const usePlacedSeats = (chartId, includeDrafts = true) => {
  const [placedSeats, setPlacedSeats] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch placed seats for chart
  const fetchPlacedSeats = useCallback(async () => {
    if (!chartId) return;
    setLoading(true);
    try {
      const url = `${API_BASE}/api/seat-map/placed-seats/${chartId}${includeDrafts ? '?include_drafts=true' : ''}`;
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPlacedSeats(data.seats || []);
      }
    } catch (err) {
      console.error('Error fetching placed seats:', err);
    } finally {
      setLoading(false);
    }
  }, [chartId, includeDrafts]);

  // Place a new seat
  const placeSeat = useCallback(async (seatData) => {
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(seatData)
      });
      const data = await res.json();
      if (res.ok) {
        setPlacedSeats(prev => [...prev, data.seat]);
        return { seat: data.seat };
      }
      return { error: data.detail || 'Failed to place seat' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Update a placed seat
  const updateSeat = useCallback(async (seatId, updates) => {
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats/${seatId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (res.ok) {
        setPlacedSeats(prev => prev.map(s => s.id === seatId ? data.seat : s));
        return { seat: data.seat };
      }
      return { error: data.detail || 'Failed to update seat' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Delete a placed seat
  const deleteSeat = useCallback(async (seatId) => {
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats/${seatId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setPlacedSeats(prev => prev.filter(s => s.id !== seatId));
        return { success: true };
      }
      const data = await res.json();
      return { error: data.detail || 'Failed to delete seat' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Clone a seat
  const cloneSeat = useCallback(async (sourceSeatId, direction, newSeatNumber, customOffset = null) => {
    try {
      const body = {
        source_seat_id: sourceSeatId,
        direction,
        new_seat_number: newSeatNumber
      };
      if (customOffset) {
        body.offset_x = customOffset.x;
        body.offset_y = customOffset.y;
      }
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats/clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        setPlacedSeats(prev => [...prev, data.seat]);
        return { seat: data.seat };
      }
      return { error: data.detail || 'Failed to clone seat' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Generate a row of seats
  const generateRow = useCallback(async (rowParams) => {
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats/generate-row`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(rowParams)
      });
      const data = await res.json();
      if (res.ok) {
        setPlacedSeats(prev => [...prev, ...data.seats]);
        return { seats: data.seats };
      }
      return { error: data.detail || 'Failed to generate row' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Assign ticket to seat
  const assignTicket = useCallback(async (seatId, ticketRef) => {
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats/${seatId}/assign-ticket`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ ticket_ref: ticketRef })
      });
      const data = await res.json();
      if (res.ok) {
        setPlacedSeats(prev => prev.map(s => s.id === seatId ? data.seat : s));
        return { seat: data.seat };
      }
      return { error: data.detail || 'Failed to assign ticket' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Bulk publish seats
  const bulkPublish = useCallback(async (seatIds, publish = true) => {
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/placed-seats/bulk-publish`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ seat_ids: seatIds, publish })
      });
      const data = await res.json();
      if (res.ok) {
        // Update local state
        const newStatus = publish ? 'published' : 'draft';
        setPlacedSeats(prev => prev.map(s => 
          seatIds.includes(s.id) ? { ...s, publication_status: newStatus } : s
        ));
        return { count: data.modified_count };
      }
      return { error: data.detail || 'Failed to bulk publish' };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  useEffect(() => {
    fetchPlacedSeats();
  }, [fetchPlacedSeats]);

  return {
    placedSeats,
    loading,
    placeSeat,
    updateSeat,
    deleteSeat,
    cloneSeat,
    generateRow,
    assignTicket,
    bulkPublish,
    refreshSeats: fetchPlacedSeats
  };
};

/**
 * Seat Template Selector Component
 */
export const SeatTemplateSelector = ({ 
  templates, 
  activeTemplate, 
  onSelect,
  disabled = false 
}) => {
  if (!templates || templates.length === 0) {
    return (
      <div className="seat-template-selector seat-template-unavailable" data-testid="seat-template-selector">
        <div className="no-templates-error">
          <div className="error-icon">⚠</div>
          <div className="error-title">Seat Templates Unavailable</div>
          <div className="error-message">
            Seat templates are unavailable in this deployment.
            Placement is temporarily disabled.
          </div>
          <div className="error-contact">
            Contact the archive administrator if this persists.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seat-template-selector" data-testid="seat-template-selector">
      <label className="template-label">Seat Template</label>
      <select 
        value={activeTemplate?.id || ''} 
        onChange={(e) => {
          const tpl = templates.find(t => t.id === e.target.value);
          onSelect(tpl);
        }}
        disabled={disabled}
        className="template-select"
        data-testid="template-select"
      >
        {templates.map(tpl => (
          <option key={tpl.id} value={tpl.id}>
            {tpl.name} ({tpl.geometry?.width}×{tpl.geometry?.height})
          </option>
        ))}
      </select>
      {activeTemplate && (
        <div className="template-info">
          <span className="template-size">
            Size: {activeTemplate.geometry?.width}×{activeTemplate.geometry?.height}px
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Rotation Control Component
 */
export const RotationControl = ({ 
  rotation = 0, 
  onChange, 
  disabled = false 
}) => {
  return (
    <div className="rotation-control" data-testid="rotation-control">
      <label className="rotation-label">Rotation: {rotation}°</label>
      <div className="rotation-buttons">
        <button 
          onClick={() => onChange(rotation - 5)} 
          disabled={disabled}
          title="Rotate -5°"
          className="rotation-btn"
          data-testid="rotate-left-5"
        >
          ↺5°
        </button>
        <button 
          onClick={() => onChange(rotation - 1)} 
          disabled={disabled}
          title="Rotate -1°"
          className="rotation-btn"
          data-testid="rotate-left-1"
        >
          ↺1°
        </button>
        <button 
          onClick={() => onChange(0)} 
          disabled={disabled}
          title="Reset rotation"
          className="rotation-btn reset"
          data-testid="rotate-reset"
        >
          ⟲
        </button>
        <button 
          onClick={() => onChange(rotation + 1)} 
          disabled={disabled}
          title="Rotate +1°"
          className="rotation-btn"
          data-testid="rotate-right-1"
        >
          1°↻
        </button>
        <button 
          onClick={() => onChange(rotation + 5)} 
          disabled={disabled}
          title="Rotate +5°"
          className="rotation-btn"
          data-testid="rotate-right-5"
        >
          5°↻
        </button>
      </div>
      <input 
        type="number"
        value={rotation}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        disabled={disabled}
        className="rotation-input"
        min="-180"
        max="180"
        step="1"
        data-testid="rotation-input"
      />
    </div>
  );
};

/**
 * Clone/Duplicate Controls Component
 */
export const CloneControls = ({ 
  selectedSeat,
  onClone,
  disabled = false 
}) => {
  const [newSeatNumber, setNewSeatNumber] = useState('');
  const [customOffset, setCustomOffset] = useState({ x: 0, y: 0 });
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    if (selectedSeat) {
      // Suggest next seat number
      const currentNum = parseInt(selectedSeat.seat) || 0;
      setNewSeatNumber(String(currentNum - 1)); // Default: decreasing
    }
  }, [selectedSeat]);

  if (!selectedSeat) {
    return (
      <div className="clone-controls disabled" data-testid="clone-controls">
        <span className="hint">Select a seat to clone</span>
      </div>
    );
  }

  return (
    <div className="clone-controls" data-testid="clone-controls">
      <label className="clone-label">Clone to:</label>
      <input 
        type="text"
        value={newSeatNumber}
        onChange={(e) => setNewSeatNumber(e.target.value)}
        placeholder="Seat #"
        className="seat-number-input"
        data-testid="clone-seat-number"
      />
      <div className="clone-direction-buttons">
        <button 
          onClick={() => onClone(selectedSeat.id, 'left', newSeatNumber)}
          disabled={disabled || !newSeatNumber}
          title="Clone Left"
          className="clone-btn"
          data-testid="clone-left"
        >
          ← Left
        </button>
        <button 
          onClick={() => onClone(selectedSeat.id, 'right', newSeatNumber)}
          disabled={disabled || !newSeatNumber}
          title="Clone Right"
          className="clone-btn"
          data-testid="clone-right"
        >
          Right →
        </button>
      </div>
      <div className="clone-direction-buttons">
        <button 
          onClick={() => onClone(selectedSeat.id, 'up', newSeatNumber)}
          disabled={disabled || !newSeatNumber}
          title="Clone Up"
          className="clone-btn"
          data-testid="clone-up"
        >
          ↑ Up
        </button>
        <button 
          onClick={() => onClone(selectedSeat.id, 'down', newSeatNumber)}
          disabled={disabled || !newSeatNumber}
          title="Clone Down"
          className="clone-btn"
          data-testid="clone-down"
        >
          Down ↓
        </button>
      </div>
      
      <button 
        onClick={() => setShowCustom(!showCustom)}
        className="toggle-custom-btn"
        data-testid="toggle-custom-offset"
      >
        {showCustom ? '▲ Hide Custom' : '▼ Custom Offset'}
      </button>
      
      {showCustom && (
        <div className="custom-offset">
          <input 
            type="number"
            value={customOffset.x}
            onChange={(e) => setCustomOffset(prev => ({ ...prev, x: parseFloat(e.target.value) || 0 }))}
            placeholder="X offset"
            className="offset-input"
            data-testid="custom-offset-x"
          />
          <input 
            type="number"
            value={customOffset.y}
            onChange={(e) => setCustomOffset(prev => ({ ...prev, y: parseFloat(e.target.value) || 0 }))}
            placeholder="Y offset"
            className="offset-input"
            data-testid="custom-offset-y"
          />
          <button 
            onClick={() => onClone(selectedSeat.id, 'custom', newSeatNumber, customOffset)}
            disabled={disabled || !newSeatNumber}
            className="clone-btn custom"
            data-testid="clone-custom"
          >
            Clone Custom
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Row Generator Component
 */
export const RowGenerator = ({ 
  template,
  chartId,
  onGenerate,
  referenceSeats = [], // Calibrated control seats for spacing calculation
  disabled = false 
}) => {
  const [section, setSection] = useState('');
  const [row, setRow] = useState('');
  const [startSeat, setStartSeat] = useState(14);
  const [seatCount, setSeatCount] = useState(14);
  const [direction, setDirection] = useState(-1); // Decreasing
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [spacingX, setSpacingX] = useState(19);
  const [spacingY, setSpacingY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [aisleAfter, setAisleAfter] = useState(''); // Comma-separated seat numbers
  const [aisleGap, setAisleGap] = useState(10);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Auto-calculate spacing from reference seats
  useEffect(() => {
    if (referenceSeats.length >= 2) {
      const sorted = [...referenceSeats].sort((a, b) => 
        parseInt(a.seat) - parseInt(b.seat)
      );
      const first = sorted[0];
      const second = sorted[1];
      const calcSpacingX = Math.abs(second.native_x - first.native_x);
      const calcSpacingY = Math.abs(second.native_y - first.native_y);
      if (calcSpacingX > 0) setSpacingX(calcSpacingX);
      if (calcSpacingY > 0) setSpacingY(calcSpacingY);
      
      // Use first seat as start position
      setStartX(first.native_x);
      setStartY(first.native_y);
      setSection(first.section);
      setRow(first.row);
      setRotation(first.rotation || 0);
    }
  }, [referenceSeats]);

  const handleGenerate = () => {
    const params = {
      template_id: template?.id,
      chart_id: chartId,
      section,
      row,
      start_seat_number: startSeat,
      seat_count: seatCount,
      numbering_direction: direction,
      start_x: startX,
      start_y: startY,
      spacing_x: spacingX,
      spacing_y: spacingY,
      rotation,
      aisle_after_seats: aisleAfter ? aisleAfter.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n)) : [],
      aisle_gap: aisleGap
    };
    onGenerate(params);
  };

  return (
    <div className="row-generator" data-testid="row-generator">
      <h4>Generate Seat Row</h4>
      
      <div className="generator-form">
        <div className="form-row">
          <label>Section</label>
          <input 
            type="text"
            value={section}
            onChange={(e) => setSection(e.target.value.toUpperCase())}
            placeholder="BB"
            className="section-input"
            data-testid="gen-section"
          />
        </div>
        
        <div className="form-row">
          <label>Row</label>
          <input 
            type="text"
            value={row}
            onChange={(e) => setRow(e.target.value)}
            placeholder="1"
            className="row-input"
            data-testid="gen-row"
          />
        </div>
        
        <div className="form-row">
          <label>Start Seat #</label>
          <input 
            type="number"
            value={startSeat}
            onChange={(e) => setStartSeat(parseInt(e.target.value) || 1)}
            className="start-seat-input"
            data-testid="gen-start-seat"
          />
        </div>
        
        <div className="form-row">
          <label>Seat Count</label>
          <input 
            type="number"
            value={seatCount}
            onChange={(e) => setSeatCount(parseInt(e.target.value) || 1)}
            min="1"
            max="50"
            className="seat-count-input"
            data-testid="gen-seat-count"
          />
        </div>
        
        <div className="form-row">
          <label>Numbering</label>
          <select 
            value={direction}
            onChange={(e) => setDirection(parseInt(e.target.value))}
            className="direction-select"
            data-testid="gen-direction"
          >
            <option value={-1}>Decreasing (14→1)</option>
            <option value={1}>Increasing (1→14)</option>
          </select>
        </div>

        <button 
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="toggle-advanced-btn"
          data-testid="toggle-advanced"
        >
          {showAdvanced ? '▲ Hide Advanced' : '▼ Show Advanced'}
        </button>

        {showAdvanced && (
          <>
            <div className="form-row">
              <label>Start X</label>
              <input 
                type="number"
                value={startX}
                onChange={(e) => setStartX(parseFloat(e.target.value) || 0)}
                className="coord-input"
                data-testid="gen-start-x"
              />
            </div>
            
            <div className="form-row">
              <label>Start Y</label>
              <input 
                type="number"
                value={startY}
                onChange={(e) => setStartY(parseFloat(e.target.value) || 0)}
                className="coord-input"
                data-testid="gen-start-y"
              />
            </div>
            
            <div className="form-row">
              <label>Spacing X</label>
              <input 
                type="number"
                value={spacingX}
                onChange={(e) => setSpacingX(parseFloat(e.target.value) || 0)}
                step="0.5"
                className="spacing-input"
                data-testid="gen-spacing-x"
              />
            </div>
            
            <div className="form-row">
              <label>Spacing Y</label>
              <input 
                type="number"
                value={spacingY}
                onChange={(e) => setSpacingY(parseFloat(e.target.value) || 0)}
                step="0.5"
                className="spacing-input"
                data-testid="gen-spacing-y"
              />
            </div>
            
            <div className="form-row">
              <label>Rotation</label>
              <input 
                type="number"
                value={rotation}
                onChange={(e) => setRotation(parseFloat(e.target.value) || 0)}
                min="-180"
                max="180"
                className="rotation-input"
                data-testid="gen-rotation"
              />
            </div>
            
            <div className="form-row">
              <label>Aisle After Seats</label>
              <input 
                type="text"
                value={aisleAfter}
                onChange={(e) => setAisleAfter(e.target.value)}
                placeholder="7, 8"
                className="aisle-input"
                data-testid="gen-aisle-after"
              />
              <small>Comma-separated seat numbers</small>
            </div>
            
            <div className="form-row">
              <label>Aisle Gap (px)</label>
              <input 
                type="number"
                value={aisleGap}
                onChange={(e) => setAisleGap(parseFloat(e.target.value) || 0)}
                className="gap-input"
                data-testid="gen-aisle-gap"
              />
            </div>
          </>
        )}

        <button 
          onClick={handleGenerate}
          disabled={disabled || !template || !section || !row}
          className="generate-btn"
          data-testid="generate-row-btn"
        >
          Preview Row Generation
        </button>
      </div>
    </div>
  );
};

/**
 * Advanced Geometry Panel Component
 * Owner-only diagnostic tool for precise coordinate entry
 * HIDDEN BY DEFAULT - Advanced users only
 */
export const AdvancedGeometryPanel = ({
  template,
  chartId,
  onPlace,
  currentCoords = null, // Current cursor position from status bar
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [section, setSection] = useState('BB');
  const [row, setRow] = useState('1');
  const [seatNum, setSeatNum] = useState('14');
  const [nativeX, setNativeX] = useState(0);
  const [nativeY, setNativeY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [isPlacing, setIsPlacing] = useState(false);
  const [lastPlaced, setLastPlaced] = useState(null);

  // Update coordinates from cursor position (passed from parent)
  useEffect(() => {
    if (currentCoords && currentCoords.x > 0 && currentCoords.y > 0) {
      setNativeX(Math.round(currentCoords.x));
      setNativeY(Math.round(currentCoords.y));
    }
  }, [currentCoords]);

  const handlePlace = async () => {
    if (!template || !chartId) return;
    
    // Validate coordinates are within reasonable bounds
    if (nativeX <= 0 || nativeY <= 0) {
      alert('Invalid coordinates. Position the cursor over the chart to set coordinates.');
      return;
    }
    
    setIsPlacing(true);
    
    const seatData = {
      chart_id: chartId,
      template_id: template.id,
      template_revision: template.revision || 1,
      section: section.toUpperCase(),
      row: row,
      seat: seatNum,
      native_x: nativeX,
      native_y: nativeY,
      rotation: rotation,
      publication_status: 'draft'
    };

    const result = await onPlace(seatData);
    setIsPlacing(false);
    
    if (result?.seat) {
      setLastPlaced(result.seat);
    }
  };

  const resetToVisual = () => {
    if (currentCoords && currentCoords.x > 0 && currentCoords.y > 0) {
      setNativeX(Math.round(currentCoords.x));
      setNativeY(Math.round(currentCoords.y));
    }
  };

  const styles = {
    container: {
      marginTop: '16px',
      border: '1px solid #ddd',
      borderRadius: '6px',
      overflow: 'hidden'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      background: '#f5f5f5',
      cursor: 'pointer',
      fontSize: '12px',
      color: '#666'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    badge: {
      fontSize: '9px',
      padding: '2px 5px',
      background: '#e0e0e0',
      borderRadius: '3px',
      color: '#888'
    },
    content: {
      padding: '12px',
      background: '#fafafa'
    },
    warning: {
      fontSize: '10px',
      color: '#c0392b',
      marginBottom: '10px',
      padding: '6px 8px',
      background: '#fdf2f2',
      borderRadius: '4px'
    },
    formRow: {
      display: 'flex',
      gap: '8px',
      marginBottom: '8px'
    },
    field: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column'
    },
    fieldLabel: {
      fontSize: '10px',
      color: '#666',
      marginBottom: '2px'
    },
    input: {
      padding: '4px 6px',
      fontSize: '12px',
      border: '1px solid #ccc',
      borderRadius: '3px',
      width: '100%',
      boxSizing: 'border-box'
    },
    buttonRow: {
      display: 'flex',
      gap: '8px',
      marginTop: '10px'
    },
    resetBtn: {
      flex: 1,
      padding: '6px 10px',
      background: '#fff',
      border: '1px solid #ccc',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '11px'
    },
    placeBtn: {
      flex: 1,
      padding: '6px 10px',
      background: '#666',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '11px'
    }
  };

  return (
    <div style={styles.container} data-testid="advanced-geometry-panel">
      <div 
        style={styles.header} 
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="advanced-panel-toggle"
      >
        <div style={styles.headerTitle}>
          <span>{isExpanded ? '▼' : '▶'}</span>
          <span>Advanced Geometry</span>
          <span style={styles.badge}>Diagnostic</span>
        </div>
      </div>
      
      {isExpanded && (
        <div style={styles.content}>
          <div style={styles.warning}>
            ⚠️ For diagnostics only. Normal placement: click directly on the map.
          </div>
          
          <div style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Section</label>
              <input 
                type="text"
                value={section}
                onChange={(e) => setSection(e.target.value.toUpperCase())}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Row</label>
              <input 
                type="text"
                value={row}
                onChange={(e) => setRow(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Seat #</label>
              <input 
                type="text"
                value={seatNum}
                onChange={(e) => setSeatNum(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Native X</label>
              <input 
                type="number"
                value={nativeX}
                onChange={(e) => setNativeX(parseFloat(e.target.value) || 0)}
                step="1"
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Native Y</label>
              <input 
                type="number"
                value={nativeY}
                onChange={(e) => setNativeY(parseFloat(e.target.value) || 0)}
                step="1"
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Rotation</label>
              <input 
                type="number"
                value={rotation}
                onChange={(e) => setRotation(parseFloat(e.target.value) || 0)}
                min="-180"
                max="180"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.buttonRow}>
            <button 
              onClick={resetToVisual}
              style={styles.resetBtn}
            >
              Reset to Cursor Position
            </button>
            <button 
              onClick={handlePlace}
              disabled={disabled || isPlacing || !template || !section || !row || !seatNum || nativeX <= 0 || nativeY <= 0}
              style={{
                ...styles.placeBtn,
                opacity: (disabled || isPlacing || nativeX <= 0) ? 0.5 : 1,
                cursor: (disabled || isPlacing || nativeX <= 0) ? 'not-allowed' : 'pointer'
              }}
            >
              {isPlacing ? 'Placing...' : 'Place at Coordinates'}
            </button>
          </div>

          {lastPlaced && (
            <div style={{
              marginTop: '8px',
              padding: '6px',
              background: '#d4edda',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#155724'
            }}>
              Placed: {lastPlaced.section} R{lastPlaced.row} S{lastPlaced.seat} @ ({lastPlaced.native_x?.toFixed(0)}, {lastPlaced.native_y?.toFixed(0)})
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Seat Properties Panel Component
 */
export const SeatPropertiesPanel = ({
  seat,
  template,
  onUpdate,
  onDelete,
  onAssignTicket,
  onPublish,
  disabled = false
}) => {
  const [ticketRef, setTicketRef] = useState('');

  useEffect(() => {
    if (seat?.assigned_ticket_ref) {
      setTicketRef(seat.assigned_ticket_ref);
    } else {
      setTicketRef('');
    }
  }, [seat]);

  if (!seat) {
    return (
      <div className="seat-properties-panel" data-testid="seat-properties-panel">
        <div className="no-selection">No seat selected</div>
      </div>
    );
  }

  return (
    <div className="seat-properties-panel" data-testid="seat-properties-panel">
      <h4>Seat Properties</h4>
      
      <div className="property-group">
        <label>ID</label>
        <span className="property-value">{seat.id}</span>
      </div>
      
      <div className="property-group">
        <label>Location</label>
        <span className="property-value">
          {seat.section} Row {seat.row} Seat {seat.seat}
        </span>
      </div>
      
      <div className="property-group">
        <label>Position</label>
        <span className="property-value">
          X: {seat.native_x?.toFixed(1)}, Y: {seat.native_y?.toFixed(1)}
        </span>
      </div>
      
      <div className="property-group">
        <label>Rotation</label>
        <span className="property-value">{seat.rotation || 0}°</span>
      </div>
      
      <div className="property-group">
        <label>Template</label>
        <span className="property-value">
          {template?.name || seat.template_id} (rev {seat.template_revision})
        </span>
      </div>
      
      <div className="property-group">
        <label>Size</label>
        <span className="property-value locked">
          {template?.geometry?.width}×{template?.geometry?.height}px 🔒
        </span>
      </div>
      
      <div className="property-group">
        <label>Status</label>
        <span className={`property-value status-${seat.publication_status}`}>
          {seat.publication_status}
        </span>
      </div>

      <div className="property-group ticket-assignment">
        <label>Assigned Ticket</label>
        <input 
          type="text"
          value={ticketRef}
          onChange={(e) => setTicketRef(e.target.value.toUpperCase())}
          placeholder="EAC-TKT-0001"
          className="ticket-ref-input"
          data-testid="ticket-ref-input"
        />
        <button 
          onClick={() => onAssignTicket(seat.id, ticketRef)}
          disabled={disabled || !ticketRef}
          className="assign-btn"
          data-testid="assign-ticket-btn"
        >
          Assign
        </button>
        {seat.assigned_ticket_ref && (
          <span className="assigned-ticket">{seat.assigned_ticket_ref}</span>
        )}
      </div>

      <div className="property-actions">
        {seat.publication_status !== 'published' && (
          <button 
            onClick={() => onPublish([seat.id], true)}
            disabled={disabled}
            className="publish-btn"
            data-testid="publish-seat-btn"
          >
            Publish
          </button>
        )}
        {seat.publication_status === 'published' && (
          <button 
            onClick={() => onPublish([seat.id], false)}
            disabled={disabled}
            className="unpublish-btn"
            data-testid="unpublish-seat-btn"
          >
            Unpublish
          </button>
        )}
        <button 
          onClick={() => onDelete(seat.id)}
          disabled={disabled}
          className="delete-btn"
          data-testid="delete-seat-btn"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

export default {
  useSeatTemplates,
  usePlacedSeats,
  SeatTemplateSelector,
  RotationControl,
  CloneControls,
  RowGenerator,
  AdvancedGeometryPanel,
  SeatPropertiesPanel
};
